import { FC, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory, useLocation, useParams } from 'react-router';
import Reference from './components/Reference';
import {
  FullscreenReferenceType,
  GlobalLocationStateType,
} from '../../types/types';
import { useFormTokenChainContext } from '../../hooks/context/useFormTokenChainContext';
import ComponentSectionContextProvider from '../../context/ComponentSectionContext';
import useRoutes from '../../hooks/useRoutes';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { getActionFromFormRouteUrl } from '../../utils/forms/getActionFromFormRouteUrl';
import { FormTokenNode } from '../../types/context';
import BreadCrumbs from '../../components/Manager/BreadCrumbs/BreadCrumbs';
import { useBreadCrumbs } from '../../hooks/useBreadCrumbs';
import { useBreadcrumbsHeight } from './hooks/useBreadcrumbsHeight';

const FormReference: FC = () => {
  const [fullscreenReference, setFullscreenReference] =
    useState<FullscreenReferenceType | null>(null);
  const { breadcrumbsRef, breadcrumbsHeight } = useBreadcrumbsHeight();
  const { search, pathname, state } = useLocation<GlobalLocationStateType>();
  const { datasetId, field } = useParams<{
    datasetId: string;
    field: string;
  }>();
  const history = useHistory();
  const routes = useRoutes();
  const { t } = useTranslate();

  const {
    getActualTokenFromUrl,
    getToken,
    formTokens,
    removeDescendantTokens,
    popToken,
    createRootToken,
    updateParentTokenAndPopActualToken,
    updateTokenValues,
  } = useFormTokenChainContext();
  const { dataset } = useDatasetContext();
  const breadcrumbs = useBreadCrumbs();

  const handleCloseFullscreenReference = useCallback(() => {
    const actualToken = getActualTokenFromUrl();

    if (actualToken) {
      const fromUrl = actualToken.fromUrl;
      const parentToken = actualToken.parentTokenId
        ? getToken(actualToken.parentTokenId)
        : null;

      // Tell the parent form to focus this field after it remounts.
      if (parentToken && fromUrl) {
        const newParentToken: FormTokenNode = {
          ...parentToken,
          meta: {
            ...parentToken.meta,
            pendingFocusField: field,
          },
        };
        updateParentTokenAndPopActualToken(newParentToken, actualToken.id);
        history.replace(fromUrl);
        return;
      }

      popToken(actualToken.id);

      if (fromUrl) {
        history.replace(fromUrl);
        return;
      }
    }

    //Fallback to dataset list
    toast(t('toasts.referenceCloseFallback'));
    history.replace(
      datasetId ? routes.datasetList(datasetId) : routes.dashboards()
    );
  }, [
    getActualTokenFromUrl,
    getToken,
    updateParentTokenAndPopActualToken,
    popToken,
    history,
    datasetId,
    routes,
    t,
    field,
  ]);

  const handleOnSubmit = useCallback(
    (value: string) => {
      const actualToken = getActualTokenFromUrl();

      if (actualToken && actualToken.parentTokenId) {
        const parentToken = getToken(actualToken.parentTokenId);

        if (parentToken) {
          // Tell the parent form to fire this field's handlers after it remounts.
          const previousValue = parentToken.values?.[field];
          const newParentToken: FormTokenNode = {
            ...parentToken,
            values: {
              ...parentToken.values,
              [field]: value,
            },
            meta: {
              ...parentToken.meta,
              pendingFullscreenHandler: { field, value, previousValue },
            },
          };
          updateParentTokenAndPopActualToken(newParentToken, actualToken.id);
          const fromUrl = actualToken.fromUrl;

          if (fromUrl) {
            history.replace(fromUrl);
            return;
          }
        }
      }

      //Fallback to dataset list
      toast(t('toasts.referenceCloseFallback'));
      history.replace(
        datasetId ? routes.datasetList(datasetId) : routes.dashboards()
      );
    },
    [
      getActualTokenFromUrl,
      getToken,
      updateParentTokenAndPopActualToken,
      history,
      routes,
      datasetId,
      t,
      field,
    ]
  );

  /*Sync fullscreen reference from token*/
  useEffect(() => {
    const actualToken = getActualTokenFromUrl();

    if (actualToken && actualToken.values) {
      setFullscreenReference(actualToken.values as FullscreenReferenceType);
    }
  }, [pathname, search, formTokens.nodes]);

  /* Remove descendant tokens when opening in the middle of the chain */
  /* Handle invalid token from url */
  useEffect(() => {
    const tokenFromUrl = getActualTokenFromUrl();

    if (tokenFromUrl) {
      removeDescendantTokens(tokenFromUrl.id);
      return;
    }

    if (!state?.redirectedFromForm) {
      const splittedPathname = pathname.split('/');
      const indexOfReferenceSection = splittedPathname.indexOf('reference');
      const formPath =
        indexOfReferenceSection === -1
          ? pathname
          : splittedPathname.slice(0, indexOfReferenceSection).join('/') || '/';

      const newRootToken = createRootToken({
        url: formPath,
        // Standalone forms (no datasetId) return to the form itself, not a dataset list.
        fromUrl: datasetId ? routes.datasetList(datasetId) : formPath,
        meta: {
          // Form label fallback
          controlLabel: datasetId
            ? `${dataset?.label} ${getActionFromFormRouteUrl(formPath)}`
            : getActionFromFormRouteUrl(formPath),
        },
      });
      history.replace(newRootToken.url, { rootToken: newRootToken });
      return;
    }
  }, []);

  /* Assign breadcrumb label to token */
  useEffect(() => {
    const actualToken = getActualTokenFromUrl();

    if (actualToken && fullscreenReference) {
      updateTokenValues({
        ...actualToken,
        meta: {
          ...actualToken.meta,
          breadCrumbLabel: fullscreenReference.label,
        },
      });
    }
  }, [fullscreenReference]);

  return (
    <ComponentSectionContextProvider componentSection="form-reference">
      <BreadCrumbs ref={breadcrumbsRef} breadcrumbs={breadcrumbs} />
      <Reference
        fullscreenReference={fullscreenReference}
        handleCloseFullscreenReference={handleCloseFullscreenReference}
        handleOnSubmit={handleOnSubmit}
        autoSelectInsertedId={state?.referenceInsertedId}
        breadcrumbsHeight={breadcrumbsHeight}
      />
    </ComponentSectionContextProvider>
  );
};

export default FormReference;
